import React from "react";
import {
  Box,
  Table,
  Paper,
  styled,
  Button,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  Chip,
} from "@material-ui/core";

import { TableRowsSkeleton } from "../../../components/src/customComponents/TableRowsSkeleton.web";
import { colors } from "../../utilities/src/Colors";

import StoreListController, {
  S,
  Props,
  IStoreItem,
} from "./StoreListController.web";

const Strings = {
  pageTitle: "Stores",
  noRecordFounds: "No Stores Found",
};

export default class StoreList extends StoreListController {
  constructor(props: Props) {
    super(props);
    this.state = {
      storeList: [],
      isLoading: false,
    } as S;
  }

  formatTime = (timeString: string) => {
    if (!timeString) return "-";
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  render() {
    const { storeList, isLoading } = this.state;

    return (
      <MainBoxWrapper>
        <Box className="pageWrapper" style={{ paddingTop: "10px" }}>
          <div style={webStyle.headerWrapper}>
            <PageTitle>{Strings.pageTitle}</PageTitle>
            <Button
              data-test-id="addStoreButton"
              className="addButton"
              onClick={() => this.props.navigation.navigate("MobileStoreCreate")}
            >
              Add Store
            </Button>
          </div>

          <TableContainer component={Paper} className="flexItem">
            <Table style={webStyle.table} aria-label="stores table">
              <TableHead className="stickyHeader">
                <TableRow style={{ background: "#204B9C", color: "#FFFFFF" }}>
                  <StyledHeaderCell>Name (EN)</StyledHeaderCell>
                  <StyledHeaderCell>Name (AR)</StyledHeaderCell>
                  <StyledHeaderCell>Phone</StyledHeaderCell>
                  <StyledHeaderCell>Status</StyledHeaderCell>
                  <StyledHeaderCell>Open Now</StyledHeaderCell>
                  <StyledHeaderCell>Rating</StyledHeaderCell>
                  <StyledHeaderCell>Position</StyledHeaderCell>
                  <StyledHeaderCell>Address (EN)</StyledHeaderCell>
                  <StyledHeaderCell>Images</StyledHeaderCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {!isLoading &&
                  storeList.map((item: IStoreItem) => {
                    const attr = item.attributes;
                    return (
                      <TableRow key={item.id} data-test-id={"store-row-" + item.id}>
                        <TableCell>{attr.name_en}</TableCell>
                        <TableCell>{attr.name_ar}</TableCell>
                        <TableCell>{attr.phone || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={attr.is_active ? "Active" : "Inactive"}
                            style={{
                              backgroundColor: attr.is_active ? "#E8F5E9" : "#FFEBEE",
                              color: attr.is_active ? "#2E7D32" : "#C62828",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={attr.is_open_now ? "Open" : "Closed"}
                            style={{
                              backgroundColor: attr.is_open_now ? "#E8F5E9" : "#FFF3E0",
                              color: attr.is_open_now ? "#2E7D32" : "#E65100",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {attr.rating ? `${attr.rating} (${attr.total_ratings})` : "-"}
                        </TableCell>
                        <TableCell>{attr.position}</TableCell>
                        <TableCell style={{ maxWidth: 200 }}>
                          {attr.address_en || "-"}
                        </TableCell>
                        <TableCell>
                          <div style={webStyle.imageRow}>
                            {attr.image_urls?.slice(0, 2).map((url, idx) => (
                              <img
                                key={idx}
                                src={url}
                                alt={`store-${idx}`}
                                style={webStyle.thumbnail}
                              />
                            ))}
                            {(!attr.image_urls || attr.image_urls.length === 0) && "-"}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>

              {isLoading && (
                <>
                  {Array(5)
                    .fill(1)
                    .map((_, index) => (
                      <TableBody key={index}>
                        <TableRow>
                          {[...Array(9)].map((_, cellIndex) => (
                            <TableRowsSkeleton key={cellIndex} />
                          ))}
                        </TableRow>
                      </TableBody>
                    ))}
                </>
              )}
            </Table>

            {storeList.length === 0 && !isLoading && (
              <NoRecordFoundText>{Strings.noRecordFounds}</NoRecordFoundText>
            )}
          </TableContainer>
        </Box>
      </MainBoxWrapper>
    );
  }
}

// Customizable Area Start
const NoRecordFoundText = styled("div")({
  fontSize: 16,
  fontWeight: 600,
  textAlign: "center",
  padding: "40px 0",
});

const PageTitle = styled("div")({
  fontFamily: "Montserrat",
  fontStyle: "normal",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "29px",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "16px",
  },
});

const StyledHeaderCell = styled(TableCell)({
  color: "#FFFFFF !important",
  fontWeight: 600,
  fontFamily: "Montserrat",
});

const MainBoxWrapper = styled("div")({
  "& .MuiTableCell-root": {
    padding: "8px 12px !important",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },
  "& .MuiTableRow-root": {
    height: "48px",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    padding: "10px 12px !important",
    fontWeight: 600,
    color: "#FFFFFF",
  },
  "& .stickyHeader": {
    zIndex: 2,
  },
  "& .addButton": {
    background: colors().cyancobaltblue,
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    color: colors().white,
    height: "56px",
    width: "184px",
    borderRadius: "8px",
    textTransform: "unset",
    "@media only screen and (max-width: 1024px)": {
      fontSize: "13px",
      width: "160px",
      height: "44px",
    },
  },
});

const webStyle = {
  table: {
    minWidth: 700,
  },
  headerWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 32,
    alignItems: "center",
    flexWrap: "wrap" as "wrap",
  },
  thumbnail: {
    width: 40,
    height: 40,
    objectFit: "cover" as "cover",
    borderRadius: 4,
    marginRight: 4,
  },
  imageRow: {
    display: "flex",
    alignItems: "center",
  },
};
// Customizable Area End
